import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Shuffle } from "lucide-react";

const MESSAGES = [
  {
    eyebrow: "You are here",
    line: "Your body is not broken. It's listening.",
    sub: "Every reset begins the moment you decide it has.",
    accent: "#22D3EE",
  },
  {
    eyebrow: "Reset",
    line: "Pain is information.",
    sub: "You are not your pain. You are the one who notices it.",
    accent: "#F59E0B",
  },
  {
    eyebrow: "Begin slow",
    line: "Every breath is a vote for the new you.",
    sub: "Slow exhales tell your nervous system the threat is over.",
    accent: "#22D3EE",
  },
  {
    eyebrow: "Permission",
    line: "You are not starting over.",
    sub: "You are starting from years of experience your body has been carrying.",
    accent: "#10B981",
  },
  {
    eyebrow: "Truth",
    line: "Awareness is the first reset.",
    sub: "Seeing the pattern is already changing it.",
    accent: "#22D3EE",
  },
  {
    eyebrow: "Trust",
    line: "Your nervous system can be reprogrammed.",
    sub: "Repetition rewires. Boring is the price of permanent.",
    accent: "#F59E0B",
  },
];

/**
 * Inspirational hero — rotating message + ambient animated visuals.
 * Auto-rotates every 8s. Tap the shuffle pill to advance manually.
 * Designed to show only at the start of the course.
 */
export default function InspirationCard() {
  const [i, setI] = useState(() => Math.floor(Math.random() * MESSAGES.length));

  useEffect(() => {
    const id = setInterval(() => setI((p) => (p + 1) % MESSAGES.length), 8000);
    return () => clearInterval(id);
  }, []);

  const msg = MESSAGES[i];

  function next() {
    setI((p) => (p + 1) % MESSAGES.length);
  }

  return (
    <div
      data-testid="inspiration-card"
      className="mt-6 relative overflow-hidden rounded-3xl border border-white/10"
      style={{
        background:
          "radial-gradient(120% 80% at 20% 0%, rgba(34,211,238,0.18), transparent 50%), radial-gradient(120% 80% at 100% 100%, rgba(245,158,11,0.14), transparent 55%), linear-gradient(160deg, rgba(10,21,48,0.85) 0%, rgba(3,16,31,0.95) 100%)",
        boxShadow: "0 24px 60px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
      }}
    >
      {/* Animated aurora blobs */}
      <motion.div
        className="absolute -top-16 -left-16 w-56 h-56 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(34,211,238,0.55), transparent 70%)" }}
        animate={{ x: [0, 20, -10, 0], y: [0, 10, -8, 0], opacity: [0.55, 0.85, 0.55] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute -bottom-20 -right-12 w-64 h-64 rounded-full blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(245,158,11,0.45), transparent 70%)" }}
        animate={{ x: [0, -16, 8, 0], y: [0, -10, 8, 0], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[
          { x: "18%", y: "22%", size: 2.5, delay: 0 },
          { x: "78%", y: "30%", size: 3, delay: 1.8 },
          { x: "44%", y: "76%", size: 2, delay: 0.9 },
          { x: "88%", y: "60%", size: 2.5, delay: 2.4 },
          { x: "12%", y: "70%", size: 2, delay: 1.4 },
          { x: "62%", y: "16%", size: 1.8, delay: 3.2 },
        ].map((p) => (
          <motion.span
            key={`${p.x}-${p.y}`}
            className="absolute rounded-full bg-[#22D3EE]"
            style={{
              left: p.x,
              top: p.y,
              width: p.size,
              height: p.size,
              boxShadow: `0 0 ${p.size * 4}px rgba(34,211,238,0.85)`,
            }}
            animate={{ opacity: [0.2, 1, 0.2], scale: [0.8, 1.2, 0.8] }}
            transition={{ duration: 3 + (p.delay % 3), repeat: Infinity, ease: "easeInOut", delay: p.delay }}
          />
        ))}
      </div>

      {/* Soft scanline grid */}
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative p-6 sm:p-7">
        {/* Top label row */}
        <div className="flex items-center justify-between mb-3">
          <div className="inline-flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ background: msg.accent, boxShadow: `0 0 12px ${msg.accent}` }}
            />
            <span
              className="bps-eyebrow"
              style={{ color: msg.accent }}
            >
              {msg.eyebrow}
            </span>
          </div>
          <button
            type="button"
            onClick={next}
            data-testid="inspiration-shuffle"
            aria-label="New message"
            className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-[#7C8FA8] hover:text-white transition-colors px-2.5 py-1 rounded-full border border-white/10 hover:border-white/30"
          >
            <Shuffle size={11} /> Next
          </button>
        </div>

        {/* Rotating message */}
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -10, filter: "blur(4px)" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <h2 className="font-display text-2xl sm:text-3xl font-light tracking-tighter text-white leading-[1.15]">
              {msg.line}
            </h2>
            <p className="text-sm text-white/75 mt-2.5 leading-relaxed">
              {msg.sub}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Sparkles footer */}
        <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2">
          <Sparkles size={12} style={{ color: msg.accent }} />
          <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#7C8FA8]">
            You showed up today. That counts.
          </span>
        </div>
      </div>
    </div>
  );
}
